"use client";

// ── The Judge product shell's own topbar (`EX-PUB11`, `EX-PUB19`) ────────────
// Judge stops being a content page inside the marketing header/footer and
// becomes its own product the way Playground is. `SiteLayout` bypasses the
// marketing chrome for `/judge` and `/contests`; this is what replaces it,
// shared by both segments' own `layout.tsx` via `JudgeShellChrome`.
//
// `EX-PUB19` (founder decision 2026-09-23, the Arena direction): the bar is
// dark on every Judge page, which is what tells a visitor they are in the
// judge, and the Judge pill stays set apart from the sections beside it.

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  ArrowLeft,
  BriefcaseBusiness,
  Code2,
  Crown,
  FileCode2,
  GraduationCap,
  ListChecks,
  Menu,
  Search,
  Trophy,
  X,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { LocalePill } from "@/components/LanguageSwitcher";
import { JudgeAccountMenu } from "./JudgeAccountMenu";
import { useAuth } from "@/components/providers/auth-provider";
import { ContestTempNav } from "@/components/auth/ContestTempNav";
import { useNow } from "@/features/contests/lib/use-now";
import { formatRemaining, remainingUntil } from "@/features/contests/lib/format";
import { contestOrRoundHref } from "@/features/contests/lib/round";
import styles from "./judge-shell-topbar.module.css";

function pathMatches(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

type NavKey = "problems" | "contests" | "leaderboard" | "interview" | "mySubmissions" | "learn";

/**
 * A Progsity round inside its window, for the bar's live chip (`fetchNextRound`,
 * `live`). `round` when it leads a round of divisions (`EX-DIV06`): the chip
 * then opens the round page, where the reader's own division is.
 */
export type JudgeLiveRound = { slug: string; title: string; endAt: string | null; round?: { slug: string } | null };

const NAV_ICON: Readonly<Record<NavKey, LucideIcon>> = {
  problems: ListChecks,
  contests: Trophy,
  leaderboard: Crown,
  interview: BriefcaseBusiness,
  mySubmissions: FileCode2,
  learn: GraduationCap,
};

/** `/` jumps to problem search, as it does on most developer sites, unless the visitor is typing. */
function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // Monaco's native EditContext receives typing on a div instead of a textarea.
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
    target.closest(".monaco-editor") !== null;
}

/**
 * A live Progsity round, one tap away. `EX-PUB22`: the round's title shows from
 * `2xl`; below it the chip says "Live" and keeps the clock, and the full label
 * stays the link's name for a screen reader at every width.
 */
function LiveChip({ round, locale }: { round: JudgeLiveRound; locale: Locale }) {
  const t = useTranslations("judge.shell");
  const now = useNow();
  const remaining = now === null ? null : remainingUntil(round.endAt, now);

  return (
    <Link
      href={contestOrRoundHref(locale, round)}
      className="relative hidden h-9 max-w-[280px] shrink items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 text-xs font-semibold text-accent-peach transition-colors duration-200 ease-out before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-[''] hover:border-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 md:inline-flex"
    >
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:animate-none [animation-iteration-count:4]" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <span className="sr-only 2xl:not-sr-only 2xl:truncate">{t("liveChip", { title: round.title })}</span>
      <span className="2xl:hidden" aria-hidden>
        {t("liveChipShort")}
      </span>
      {remaining && remaining.total > 0 ? (
        <span className="shrink-0 font-mono font-medium tabular-nums opacity-85">{formatRemaining(remaining)}</span>
      ) : null}
    </Link>
  );
}

export function JudgeShellTopbar({ liveRound = null }: { liveRound?: JudgeLiveRound | null }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale() as Locale;
  const t = useTranslations("judge.shell");
  const tNav = useTranslations("nav");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  const base = `/${locale}`;
  // `RV-20`: Log in in the phone menu comes back to this page.
  const signInHref = useSignInHref(locale);
  const judgeHref = `${base}/judge`;
  const problemsHref = `${base}/judge/problems`;
  const leaderboardHref = `${base}/judge/leaderboard`;
  const submissionsHref = `${base}/judge/submissions`;
  const interviewHref = `${base}/judge/interview`;

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setMobileOpen(false);
      mobileMenuButtonRef.current?.focus();
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  // The bar gains a brighter hairline once the page moves under it.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function focusSearch(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing) return;
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || typingInto(event.target)) return;
      event.preventDefault();
      const field = document.querySelector("[data-judge-search]");
      if (field instanceof HTMLInputElement) {
        field.focus();
        return;
      }
      router.push(`${problemsHref}?focus=search`);
    }

    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, [router, problemsHref]);

  const links: { key: NavKey; href: string }[] = [
    { key: "problems", href: problemsHref },
    { key: "contests", href: `${base}/contests` },
    { key: "leaderboard", href: leaderboardHref },
    // `EX-FNC07`: Interview Prep, the free list of function problems (board H).
    { key: "interview", href: interviewHref },
    // `EX-PUB20`: inside the Judge. `/dashboard/submissions` swapped this bar for the dashboard's own.
    { key: "mySubmissions", href: submissionsHref },
    // `LN-NAV01`: programmers gather here, so Learn gets a cross-link too;
    // absent for a `contest_temp` account because this whole shell already
    // returns `ContestTempNav` for that account kind, below.
    { key: "learn", href: `${base}/learn` },
  ];

  /**
   * `EX-PUB13`: `/judge` is now the homepage, not the problems section, so it
   * is excluded here — the wordmark alone carries the "you are home" state.
   * A problem detail page (`/judge/[slug]`) still counts as "problems",
   * exactly as it did when `/judge` itself was the catalogue.
   */
  function isActive(key: NavKey, href: string): boolean {
    if (key === "problems") {
      return (
        pathname !== judgeHref &&
        pathMatches(pathname, judgeHref) &&
        !pathMatches(pathname, leaderboardHref) &&
        !pathMatches(pathname, submissionsHref) &&
        !pathMatches(pathname, interviewHref)
      );
    }
    // `EX-DIV06`: a round page lives at `/rounds/`, beside the contests it ties.
    if (key === "contests") return pathMatches(pathname, href) || pathMatches(pathname, `${base}/rounds`);
    return pathMatches(pathname, href);
  }

  if (user?.accountKind === "contest_temp") return <ContestTempNav />;
  return (
    <header
      className={cn(
        "sticky top-0 z-30 shrink-0 border-b bg-neutral-950 transition-colors duration-200 ease-out",
        scrolled ? "border-white/15" : "border-white/[0.07]",
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-3 sm:gap-5">
        <div className="flex h-full min-w-0 items-center gap-3 lg:gap-5">
          {/*
            Every control on this bar takes taps over 44 px at every width, a
            tablet being a touch screen too, mostly without growing (a
            transparent `::before`). On a phone (the 2026-10-02 report at 390
            wide: the Judge pill ran under the language switch) the pill is its
            icon alone below 360 wide and the language switch lives in the
            menu below `sm`.
          */}
          <Link
            href={base}
            className="relative flex shrink-0 items-center rounded-md outline-none before:absolute before:inset-x-0 before:-inset-y-3 before:content-[''] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950"
            aria-label={t("progsityHome")}
          >
            <Image
              src="/logo-white.svg"
              alt=""
              width={112}
              height={26}
              className="h-[22px] w-auto sm:h-[26px]"
              priority
            />
          </Link>

          <span className="hidden h-6 w-px shrink-0 bg-white/15 sm:block" aria-hidden />

          <Link
            href={judgeHref}
            aria-label={t("judgeHome")}
            aria-current={pathname === judgeHref ? "page" : undefined}
            className={cn(
              "relative inline-flex h-9 shrink-0 items-center gap-2 rounded-xl border px-1 font-[family-name:var(--font-ubuntu)] text-sm font-bold tracking-tight text-accent-peach outline-none transition-colors duration-200 ease-out before:absolute before:-inset-1.5 before:content-[''] sm:h-[38px] sm:gap-2.5 sm:text-[15px]",
              pathname === judgeHref
                ? "border-primary/50 bg-primary/15"
                : "border-primary/30 bg-primary/10 hover:border-primary/60",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950",
            )}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-white" aria-hidden>
              <Code2 className="h-4 w-4" strokeWidth={2.4} />
            </span>
            <span className={cn("hidden pr-2 sm:pr-2.5", styles.wordmarkRoomy)}>{t("wordmark")}</span>
          </Link>

          {/*
            `EX-PUB22`: from `xl`, with the icons from 1400 wide (from `2xl`
            beside a live round's chip). Five links, the search pill and the
            language pill ran out of room from 1024 to about 1300 wide, and the
            last link slid under the search; a live round's chip took more.
            Below `xl` the links live in the menu. `EX-FNC07` made it six
            links (Interview Prep): the icons now come back from 1600 (from
            `3xl` beside a live round's chip) and the wide search pill from
            `2xl`, measured on the drawn bar at 1280, 1440 and 1600.
          */}
          <nav className="hidden h-full items-stretch gap-0.5 xl:flex" aria-label={t("navLabel")}>
            {links.map((link) => {
              const Icon = NAV_ICON[link.key];
              const active = isActive(link.key, link.href);
              return (
                <Link
                  key={link.key}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "group relative inline-flex items-center gap-2 whitespace-nowrap px-3 text-sm font-medium outline-none transition-colors duration-200 ease-out",
                    "after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:origin-center after:rounded-t after:bg-primary after:transition-transform after:duration-200 after:ease-out",
                    active
                      ? "text-white after:scale-x-100"
                      : "text-neutral-300 after:scale-x-0 hover:text-white hover:after:scale-x-100 focus-visible:after:scale-x-100",
                    "focus-visible:text-white",
                  )}
                >
                  <Icon
                    className={cn(
                      "hidden h-4 w-4 transition-colors duration-200 ease-out",
                      liveRound ? "3xl:block" : styles.iconRoomy,
                      active ? "text-accent-peach" : "text-neutral-500 group-hover:text-accent-peach",
                    )}
                    aria-hidden
                  />
                  {t(link.key)}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
          {liveRound ? <LiveChip round={liveRound} locale={locale} /> : null}

          {/* `EX-PUB20`: the problems page has its own search box, and two on one screen was one too many.
              `EX-PUB22`: while a round is live the pill stays an icon, so the round's chip has the room. */}
          {pathname === problemsHref ? null : (
            <Link
              href={problemsHref}
              aria-label={t("search")}
              className={cn(
                "relative hidden h-9 items-center gap-2.5 rounded-[10px] border border-white/10 bg-white/[0.04] pl-3 pr-1.5 text-[13px] text-neutral-400 transition-colors duration-200 ease-out before:absolute before:-inset-1.5 before:content-[''] hover:border-primary/50 hover:text-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 md:inline-flex",
                liveRound ? null : "2xl:w-56",
              )}
            >
              <Search className="h-4 w-4 shrink-0" aria-hidden />
              <span className={cn("hidden", liveRound ? null : "2xl:inline")}>{t("search")}</span>
              <kbd
                className={cn(
                  "ml-auto hidden h-[22px] min-w-[22px] items-center justify-center rounded-md border border-white/10 bg-white/[0.06] px-1.5 font-mono text-[11px] text-neutral-300",
                  liveRound ? null : "2xl:inline-flex",
                )}
              >
                /
              </kbd>
            </Link>
          )}

          <div className="hidden sm:block">
            <LocalePill locale={locale} tone="dark" size="bar" />
          </div>
          <JudgeAccountMenu locale={locale} />
          <Button
            ref={mobileMenuButtonRef}
            type="button"
            variant="outlineDark"
            size="icon"
            aria-label={mobileOpen ? t("closeMenu") : t("openMenu")}
            aria-expanded={mobileOpen}
            aria-controls="judge-mobile-navigation"
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </Button>
        </div>
      </Container>

      <div
        id="judge-mobile-navigation"
        className={cn(
          "overflow-hidden border-t border-white/10 bg-neutral-950 transition-all duration-200 ease-out xl:hidden",
          mobileOpen ? "max-h-[32rem]" : "max-h-0 border-t-0",
        )}
        aria-hidden={!mobileOpen}
      >
        <Container className="py-3">
          <nav className="flex flex-col gap-1" aria-label={t("navLabel")}>
            <Link
              href={judgeHref}
              tabIndex={mobileOpen ? 0 : -1}
              aria-current={pathname === judgeHref ? "page" : undefined}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "flex min-h-11 items-center gap-2 rounded-[10px] px-3 text-sm font-semibold transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                pathname === judgeHref ? "bg-primary/15 text-accent-peach" : "text-white hover:bg-white/5",
              )}
            >
              <Code2 className="h-4 w-4 text-primary" aria-hidden />
              {t("judgeHome")}
            </Link>

            {links.map((link) => {
              const Icon = NAV_ICON[link.key];
              const active = isActive(link.key, link.href);
              return (
                <Link
                  key={link.key}
                  href={link.href}
                  tabIndex={mobileOpen ? 0 : -1}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex min-h-11 items-center gap-2.5 rounded-[10px] px-3 text-sm font-medium transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    active ? "bg-primary/15 text-accent-peach" : "text-neutral-200 hover:bg-white/5",
                  )}
                >
                  <Icon className="h-4 w-4 text-neutral-500" aria-hidden />
                  {t(link.key)}
                </Link>
              );
            })}

            <div className="my-2 h-px bg-white/10" aria-hidden />

            <Link
              href={base}
              tabIndex={mobileOpen ? 0 : -1}
              onClick={() => setMobileOpen(false)}
              className="flex min-h-11 items-center gap-2 rounded-[10px] px-3 text-sm font-semibold text-neutral-300 transition-colors duration-200 ease-out hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ArrowLeft className="h-4 w-4 text-neutral-500" aria-hidden />
              {t("backToProgsity")}
            </Link>
          </nav>

          {/* Below `sm` the language switch is here instead of on the bar, drawn only while the menu is open so a closed menu holds nothing to tab to. */}
          {mobileOpen ? (
            <div className="mt-1 flex min-h-11 items-center justify-between gap-3 px-3 sm:hidden">
              <span className="text-sm font-medium text-neutral-300">{tNav("language")}</span>
              <LocalePill locale={locale} tone="dark" size="md" />
            </div>
          ) : null}

          {/* The bar's Log in shows from `sm`; below it a signed-out visitor finds it here. */}
          {mobileOpen && !isLoading && (!isAuthenticated || !user) ? (
            <Button asChild variant="outlineDark" size="lg" className="mt-3 w-full sm:hidden">
              <Link href={signInHref} onClick={() => setMobileOpen(false)}>
                {tNav("login")}
              </Link>
            </Button>
          ) : null}
        </Container>
      </div>
    </header>
  );
}
